import { ReactNode } from "react"
import { useModuleContext } from "@edemics/contexts/module-provider"
import { ModuleUnavailablePage } from "./module-unavailable-page"
import { ModulePremiumUpsellPage } from "./module-premium-upsell-page"

interface ModuleGateProps {
  /**
   * The module keys this boundary requires — pass one, or several if
   * a page depends on more than one (rare). Every key must be enabled
   * for the tenant OR the wrapped children don't render.
   */
  keys: string | string[]
  /**
   * Human-readable name shown on the ModuleUnavailablePage when this
   * boundary blocks. Falls back to the first key when omitted.
   */
  moduleName?: string
  /**
   * What to render when the gate rejects. Defaults to
   * <ModuleUnavailablePage /> — a proper page, not a redirect, so
   * deep-links / bookmarks land on an honest URL that support can see.
   * Pass a ReactNode to render inline instead.
   */
  fallback?: ReactNode
  children: ReactNode
}

/**
 * Boundary that hides a `<Route>` or subtree when the tenant's
 * super-admin has disabled the required module(s). Composes with the
 * BE ModuleAccessInterceptor: the FE hides the door, the BE bolts it.
 *
 * Usage inside App.tsx:
 *   <Route path="/hrms/*" element={
 *     <ModuleGate keys="hrms" moduleName="HRMS">
 *       <HrmsRoutes />
 *     </ModuleGate>
 *   } />
 *
 * For a page that needs multiple modules:
 *   <ModuleGate keys={["academics", "communication"]}>...</ModuleGate>
 *
 * Deep-link behavior (2026-08-27): a disabled route no longer
 * redirects to /dashboard. Instead it renders the
 * ModuleUnavailablePage inline so the URL stays intact — bookmarks
 * see an honest "not available" state and support can reproduce the
 * bad state from a screenshot. To restore the old redirect behavior
 * on a per-route basis, pass fallback={<Navigate to="/dashboard"/>}.
 */
export function ModuleGate({ keys, moduleName, fallback, children }: ModuleGateProps) {
  const { isModuleEnabled, isModulePremiumLocked, tenantEnabledModules } =
    useModuleContext()

  const required = Array.isArray(keys) ? keys : [keys]

  // While the tenant list hasn't arrived yet (pre-login or in-flight
  // /auth/me), don't flash a "not available" page. Render children
  // and let the BE 403 catch it if truly disabled. Once the list is
  // present, apply the check.
  if (tenantEnabledModules === null) {
    return <>{children}</>
  }

  const allowed = required.every((k) => isModuleEnabled(k))
  if (allowed) return <>{children}</>

  if (fallback !== undefined) return <>{fallback}</>

  // H4 (2026-09-03) — if ANY of the required keys are premium-locked
  // (versus super-admin-off), route to the upsell page. Mixed cases
  // are rare — a page that needs both a premium and a free module —
  // and premium wins so the user sees the action they can take.
  const premiumLocked = required.some((k) => isModulePremiumLocked(k))
  if (premiumLocked) {
    return <ModulePremiumUpsellPage moduleName={moduleName} requiredKeys={required} />
  }

  return <ModuleUnavailablePage moduleName={moduleName} requiredKeys={required} />
}

/**
 * Hook a module page can call to self-verify. Returns the same
 * decision the BE interceptor makes. Use when a page renders inside
 * a shell that ModuleGate doesn't wrap (or as belt-and-braces on
 * top of it).
 *
 *   const { allowed, unavailablePage } = useModuleGuard("hrms", "HRMS")
 *   if (!allowed) return unavailablePage
 *   ... normal render ...
 *
 * The `unavailablePage` is a fully-rendered ModuleUnavailablePage the
 * caller can just return; it embeds the module name so nothing more
 * needs to be threaded through.
 */
export function useModuleGuard(
  keys: string | string[],
  moduleName?: string,
): { allowed: boolean; unavailablePage: ReactNode } {
  const { isModuleEnabled, isModulePremiumLocked, tenantEnabledModules } =
    useModuleContext()
  const required = Array.isArray(keys) ? keys : [keys]

  // Session-restore window — render as allowed so we don't flash the
  // "not available" page while /auth/me is in flight.
  if (tenantEnabledModules === null) {
    return { allowed: true, unavailablePage: null }
  }

  const allowed = required.every((k) => isModuleEnabled(k))
  // H4 — mirror the ModuleGate branch: premium lock wins over the
  // generic unavailable page.
  const premiumLocked = required.some((k) => isModulePremiumLocked(k))
  return {
    allowed,
    unavailablePage: premiumLocked ? (
      <ModulePremiumUpsellPage moduleName={moduleName} requiredKeys={required} />
    ) : (
      <ModuleUnavailablePage moduleName={moduleName} requiredKeys={required} />
    ),
  }
}
